import React, { useEffect, useRef } from "react";

import { useUserEvents } from "hooks";

import Icon_MidstreamContributingWells from "components/icons/Icon_MidstreamContributingWells";
import { NetworkFraction } from "components/map/hooks/useGetFacilityNetworkFraction";
import {
  midstreamNavigatorActions,
  useMidstreamNavigatorContext
} from "components/midstream/midstream-navigator/context/MidstreamNavigatorContext";

import { FilterKey, useFilterButtonContext } from "../context/FilterButtonContext";
import { BaseMidstreamFilterButton, FilterConfig } from "../filters/BaseFilterButton";
import { useFacilityAndWellsFilter } from "../hooks/useFacilityAndWellsFilter";
import { useFilterUndo } from "../hooks/useFilterUndo";

export const FacilityAndWellsButton: React.FC = () => {
  const [state, dispatch] = useMidstreamNavigatorContext();
  const {
    wellFilterCount,
    getIsDisabled,
    clearOtherFacilityFilters,
    withUserAction,
    useExternalFilterMonitoring,
    MonitoringStrategies
  } = useFilterButtonContext();
  const {
    filterToFacilityAndContributingWells,
    removeFilterToFacilityAndContributingWells,
    networkFractions,
    selectedFacilityUwi
  } = useFacilityAndWellsFilter();
  const { undoFilters } = useFilterUndo();
  const { sendMidstreamNavigatorButtonToggledEvent } = useUserEvents();

  const pendingEventDataRef = useRef<{
    selectedFacility: string;
    previousCount: number;
    networkFractions: NetworkFraction[];
  } | null>(null);

  const config: FilterConfig = {
    icon: <Icon_MidstreamContributingWells />,
    tooltip: "Filter To Facility and Contributing Wells",
    testId: `filter-button-${FilterKey.FacilityAndWells}`
  };

  const isActive = state.activeFilters?.includes(FilterKey.FacilityAndWells) ?? false;
  const isDisabled = getIsDisabled(FilterKey.FacilityAndWells, isActive);
  const disabledReason = isDisabled ? "Select a facility to filter" : null;

  // Use shared external filter monitoring
  useExternalFilterMonitoring(
    FilterKey.FacilityAndWells,
    isActive,
    MonitoringStrategies.wellFilter
  );

  useEffect(() => {
    if (pendingEventDataRef.current && isActive) {
      const {
        selectedFacility,
        previousCount,
        networkFractions: storedFractions
      } = pendingEventDataRef.current;

      // Send event if wellFilterCount is defined OR if networkFractions is null (no contributing wells)
      const shouldSendEvent =
        (wellFilterCount !== undefined && wellFilterCount !== previousCount) ||
        networkFractions === null ||
        networkFractions?.length === 0;

      if (shouldSendEvent) {
        const contributingWells = (networkFractions || storedFractions || []).map(
          (fraction) => fraction.wellId
        );

        sendMidstreamNavigatorButtonToggledEvent({
          filterKey: FilterKey.FacilityAndWells,
          selectedFacility: selectedFacility,
          wellFilterCount: wellFilterCount,
          contributingWells: contributingWells
        });

        pendingEventDataRef.current = null;
      }
    }
  }, [
    wellFilterCount,
    networkFractions,
    isActive,
    sendMidstreamNavigatorButtonToggledEvent
  ]);

  // Auto-cleanup when button becomes disabled while active
  useEffect(() => {
    if (isDisabled && isActive) {
      // Button became disabled while active - clean up the filter
      dispatch(midstreamNavigatorActions.removeFilter(FilterKey.FacilityAndWells));
      removeFilterToFacilityAndContributingWells();
    }
  }, [isDisabled, isActive, dispatch, undoFilters]);

  const handleToggle = () => {
    withUserAction(() => {
      if (!isActive) {
        // Use shared function to clear other facility filters (mutual exclusion)
        clearOtherFacilityFilters(FilterKey.FacilityAndWells);

        dispatch(midstreamNavigatorActions.addFilter(FilterKey.FacilityAndWells));

        // Store selected facility before filtering for analytics
        pendingEventDataRef.current = {
          selectedFacility: selectedFacilityUwi || "",
          previousCount: wellFilterCount || 0,
          networkFractions: networkFractions || []
        };
        filterToFacilityAndContributingWells();
      } else {
        dispatch(midstreamNavigatorActions.removeFilter(FilterKey.FacilityAndWells));
        removeFilterToFacilityAndContributingWells(true);
      }
    });
  };

  return (
    <BaseMidstreamFilterButton
      config={config}
      isActive={isActive}
      isDisabled={isDisabled}
      onToggle={handleToggle}
      disabledReason={disabledReason}
    />
  );
};
